import { type ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { type ITableRowProps, TableRow } from 'shared/ui/TableNew/ui/TableRow/TableRow'
import { type IRowDefault, type ITableProps, type TableStore } from 'shared/ui/TableNew'
import { type IRow, type IRowExtractType } from 'shared/ui/TableNew/store/types'
import { TableRowChild } from 'shared/ui/TableNew/ui/TableRow/TableRowChild'
import { TableRowMore } from 'shared/ui/TableNew/ui/TableRow/TableRowMore'

export type IExpandedTableRowProps<TRow extends IRowDefault> = Omit<
  ITableRowProps<TRow> & {
    store: TableStore<TRow>
    hideExpandColumnsIds?: ITableProps<TRow>['hideExpandColumnsIds']
    onRowChildClick?: ITableProps<TRow>['onRowChildClick']
    getRowChildLink?: ITableProps<TRow>['getRowChildLink']
  },
  'unpinnedColumns' | 'pinnedColumns'
>

export type IExpandedRowItem<TRow extends IRowDefault> =
  | { kind: 'row'; key: string; row: IRow<TRow> }
  | { kind: 'child'; key: string; row: IRowExtractType<TRow>; parent: IRow<TRow> }
  | { kind: 'more'; key: string; parent: IRow<TRow> }

export const getRowItemKey = (row: Pick<IRowDefault, 'id'>) => `row:${row.id}`

export const getExpandedRowItems = <TRow extends IRowDefault>(
  row: IRow<TRow>
): IExpandedRowItem<TRow>[] => {
  const items: IExpandedRowItem<TRow>[] = [{ kind: 'row', key: getRowItemKey(row), row }]

  if (!row.expanded) return items

  row.rows?.forEach((child) => {
    items.push({ kind: 'child', key: `child:${row.id}:${child.id}`, row: child, parent: row })
  })

  if (row.isMoreExpand) items.push({ kind: 'more', key: `more:${row.id}`, parent: row })

  return items
}

export const getExpandedRowItemGroupRow = <TRow extends IRowDefault>(
  item: IExpandedRowItem<TRow>
): IRow<TRow> => (item.kind === 'row' ? item.row : item.parent)

export const renderExpandedRowItem = <TRow extends IRowDefault>(
  item: IExpandedRowItem<TRow>,
  props: IExpandedTableRowProps<TRow>,
  rowRef?: ITableRowProps<TRow>['rowRef']
): ReactNode => {
  const { store, ...rest } = props
  const { columnsStore } = store
  const { columns, columnsChild, getUnpinnedColumns, getPinnedColumns } = columnsStore

  if (item.kind === 'more') {
    return <TableRowMore key={item.key} row={item.parent} />
  }

  if (item.kind === 'child') {
    item.row.setColumnsKeys(item.parent.columnsKeys)

    return (
      <TableRowChild
        {...rest}
        key={item.key}
        row={item.row}
        rowRef={rowRef}
        pinnedColumns={getPinnedColumns(columnsChild)}
        unpinnedColumns={getUnpinnedColumns(columnsChild)}
        onRowClick={props.onRowChildClick}
        getRowLink={props.getRowChildLink}
      />
    )
  }

  return (
    <TableRow
      {...rest}
      key={item.key}
      row={item.row}
      rowRef={rowRef}
      pinnedColumns={getPinnedColumns(columns)}
      unpinnedColumns={getUnpinnedColumns(columns)}
    />
  )
}

export const ExpandedTableRow = observer(
  <TRow extends IRowDefault>({ rowRef, ...props }: IExpandedTableRowProps<TRow>) => (
    <>
      {getExpandedRowItems(props.row).map((item) =>
        renderExpandedRowItem(item, props, item.kind === 'row' ? rowRef : undefined)
      )}
    </>
  )
)
